/**
 * Song tabs ("Opin lög") — the pure model (unit tested). The song page shows a strip of the songs and
 * lists opened recently on this device by this user:
 *  - a SONG tab per song opened on its own (Öll lög, search);
 *  - ONE tab per folder / setlist opened from it (CollectionScreen, Fyrra / Næsta, the "Næsta lag"
 *    chip, the list drawer). It follows the list's current entry by entry id (a reorder or a repeated
 *    song never confuses it); its songs are in the drawer, in list order. An event's practice
 *    ("Lög æfingarinnar", shared/program.ts) is such a list too, under its virtual id p-<eventId>.
 *
 * A new tab is added on the right and switching never reorders (predictable positions). At most
 * MAX_TABS: the one used longest ago that is not the active one is dropped. Each tab remembers the
 * sheet shown (per song for a list tab) and the page it was left at, restored only when the strip
 * switches to it. Ids only — titles always come from the (wiped-on-user-switch) query cache, so
 * nothing readable is kept for the next user of a shared iPad (store.ts keeps the doc per user).
 *
 * Every function returns the SAME doc when nothing changed (the store skips the write then).
 */
import type { CollectionEntryDTO } from '../../shared/dto/library';
import { practiceEventId } from '../../shared/program';
import { orderedEntries, resolveSetlistIndex, setlistSearch } from '../viewer/setlist';

export const TABS_VERSION = 1;
export const MAX_TABS = 8;
/** Remembered sheets per list tab (oldest dropped first): a long folder must not grow the doc forever. */
export const MAX_LIST_SHEETS = 100;

/** The page a tab was left at (0-based); restored only for the same file (any revision). */
export interface PageMemo {
  fileId: string;
  rev: number;
  page: number;
}

export type SongTabKey = `s:${string}`;
export type CollectionTabKey = `c:${string}`;
export type TabKey = SongTabKey | CollectionTabKey;

export interface SongTab {
  kind: 'song';
  key: SongTabKey;
  songId: string;
  /** The sheet shown last (null: none yet — the default rule applies). */
  sheetId: string | null;
  page: PageMemo | null;
  usedAt: number;
}

export interface CollectionTab {
  kind: 'collection';
  key: CollectionTabKey;
  collectionId: string;
  /** The current entry (null: unknown — the first entry opens). */
  entryId: string | null;
  songId: string | null;
  /** The sheet shown last, per song of the list. */
  sheets: Record<string, string>;
  /** The page the current entry was left at. */
  page: PageMemo | null;
  usedAt: number;
}

export type Tab = SongTab | CollectionTab;

export interface TabsDoc {
  v: typeof TABS_VERSION;
  userId: string;
  /** Strip order (left → right). */
  items: Tab[];
}

/** What the song page shows: a song on its own, or an entry of a list. */
export type VisitTarget = { kind: 'song'; songId: string } | { kind: 'collection'; collectionId: string; entryId: string; songId: string };

export const songTabKey = (songId: string): SongTabKey => `s:${songId}`;
export const collectionTabKey = (collectionId: string): CollectionTabKey => `c:${collectionId}`;
export const keyOf = (t: VisitTarget): TabKey => (t.kind === 'song' ? songTabKey(t.songId) : collectionTabKey(t.collectionId));

export function emptyDoc(userId: string): TabsDoc {
  return { v: TABS_VERSION, userId, items: [] };
}

export function findTab(doc: TabsDoc, key: TabKey | null): Tab | undefined {
  return key ? doc.items.find((t) => t.key === key) : undefined;
}

/** The sheet a tab remembers for `songId` (a song tab only for its own song). */
export function sheetOf(tab: Tab, songId: string): string | null {
  if (tab.kind === 'song') return tab.songId === songId ? tab.sheetId : null;
  return Object.prototype.hasOwnProperty.call(tab.sheets, songId) ? (tab.sheets[songId] ?? null) : null;
}

function replaceTab(doc: TabsDoc, tab: Tab): TabsDoc {
  return { ...doc, items: doc.items.map((t) => (t.key === tab.key ? tab : t)) };
}

/** Drops the tabs used longest ago (never `keep`) until at most MAX_TABS remain. */
function capTabs(items: Tab[], keep: TabKey): Tab[] {
  const out = [...items];
  while (out.length > MAX_TABS) {
    let drop = -1;
    for (let k = 0; k < out.length; k++) {
      if (out[k]!.key === keep) continue;
      if (drop < 0 || out[k]!.usedAt < out[drop]!.usedAt) drop = k;
    }
    if (drop < 0) break;
    out.splice(drop, 1);
  }
  return out;
}

/**
 * The song page showed `target`: its tab is marked used (a list tab moves to the new entry — its
 * page memo belonged to the previous one), or a new tab is added on the right. Never reorders.
 */
export function visit(doc: TabsDoc, target: VisitTarget, now: number): TabsDoc {
  const key = keyOf(target);
  const existing = findTab(doc, key);
  if (existing) {
    if (existing.kind === 'song') return replaceTab(doc, { ...existing, usedAt: now });
    if (target.kind !== 'collection') return doc;
    const moved = existing.entryId !== target.entryId || existing.songId !== target.songId;
    return replaceTab(doc, { ...existing, entryId: target.entryId, songId: target.songId, page: moved ? null : existing.page, usedAt: now });
  }
  const tab: Tab =
    target.kind === 'song'
      ? { kind: 'song', key: songTabKey(target.songId), songId: target.songId, sheetId: null, page: null, usedAt: now }
      : {
          kind: 'collection',
          key: collectionTabKey(target.collectionId),
          collectionId: target.collectionId,
          entryId: target.entryId,
          songId: target.songId,
          sheets: {},
          page: null,
          usedAt: now,
        };
  return { ...doc, items: capTabs([...doc.items, tab], key) };
}

/**
 * Closes a tab. `next` = the tab to open instead: the nearest one to its right, else to its left
 * (only tabs `isVisible` accepts — a tab the user cannot see is skipped); null = none left.
 */
export function close(doc: TabsDoc, key: TabKey, isVisible: (t: Tab) => boolean = () => true): { doc: TabsDoc; next: Tab | null } {
  const at = doc.items.findIndex((t) => t.key === key);
  if (at < 0) return { doc, next: null };
  const items = doc.items.filter((t) => t.key !== key);
  let next: Tab | null = null;
  for (let k = at; k < items.length && !next; k++) if (isVisible(items[k]!)) next = items[k]!;
  for (let k = at - 1; k >= 0 && !next; k--) if (isVisible(items[k]!)) next = items[k]!;
  return { doc: { ...doc, items }, next };
}

/**
 * Removes tabs for songs / lists missing from a list fetched from the server THIS session (null = not
 * fetched yet: a list restored from storage may be days old, so it never removes anything). List
 * tabs also forget the sheets of songs that are gone. A practice's tab is never in the list of
 * folders / setlists: it stays (it shows only while its practice list is cached and visible).
 */
export function prune(doc: TabsDoc, seen: { songs: ReadonlySet<string> | null; collections: ReadonlySet<string> | null }): TabsDoc {
  const { songs, collections } = seen;
  if (!songs && !collections) return doc;
  let changed = false;
  const items: Tab[] = [];
  for (const t of doc.items) {
    if (t.kind === 'song') {
      if (songs && !songs.has(t.songId)) changed = true;
      else items.push(t);
      continue;
    }
    if (collections && !collections.has(t.collectionId) && !practiceEventId(t.collectionId)) {
      changed = true;
      continue;
    }
    const gone = songs ? Object.keys(t.sheets).filter((id) => !songs.has(id)) : [];
    if (gone.length === 0) {
      items.push(t);
      continue;
    }
    changed = true;
    const sheets = { ...t.sheets };
    for (const id of gone) delete sheets[id];
    items.push({ ...t, sheets });
  }
  return changed ? { ...doc, items } : doc;
}

/** The tab `key` showed sheet `sheetId` of `songId` (a song tab only for its own song). */
export function setSheet(doc: TabsDoc, key: TabKey, songId: string, sheetId: string): TabsDoc {
  const tab = findTab(doc, key);
  if (!tab || sheetOf(tab, songId) === sheetId) return doc;
  if (tab.kind === 'song') return tab.songId === songId ? replaceTab(doc, { ...tab, sheetId }) : doc;
  const sheets = { ...tab.sheets };
  delete sheets[songId]; // re-inserted last: the newest choice is dropped last
  sheets[songId] = sheetId;
  const ids = Object.keys(sheets);
  for (const id of ids.slice(0, Math.max(0, ids.length - MAX_LIST_SHEETS))) delete sheets[id];
  return replaceTab(doc, { ...tab, sheets });
}

/** The tab `key` is now at `memo` (the viewer's current page). */
export function setPage(doc: TabsDoc, key: TabKey, memo: PageMemo): TabsDoc {
  const tab = findTab(doc, key);
  if (!tab) return doc;
  const p = tab.page;
  if (p && p.fileId === memo.fileId && p.rev === memo.rev && p.page === memo.page) return doc;
  return replaceTab(doc, { ...tab, page: { fileId: memo.fileId, rev: memo.rev, page: memo.page } });
}

/**
 * The server said the song is gone (deleted, or no longer visible): its own tab goes at once; list
 * tabs forget its sheet and, when it is their current song, the page.
 */
export function forgetSong(doc: TabsDoc, songId: string): TabsDoc {
  let changed = false;
  const items: Tab[] = [];
  for (const t of doc.items) {
    if (t.kind === 'song') {
      if (t.songId === songId) changed = true;
      else items.push(t);
      continue;
    }
    const hasSheet = Object.prototype.hasOwnProperty.call(t.sheets, songId);
    const current = t.songId === songId && t.page !== null;
    if (!hasSheet && !current) {
      items.push(t);
      continue;
    }
    changed = true;
    const sheets = { ...t.sheets };
    delete sheets[songId];
    items.push({ ...t, sheets, page: current ? null : t.page });
  }
  return changed ? { ...doc, items } : doc;
}

/**
 * Where a tab opens. A list tab: its entry (by id), else the nearest occurrence of its song, else
 * the list's first entry (all over the list as this viewer gets it, so ?i= matches
 * CollectionScreen's links), else the list screen itself (a practice: its event).
 */
export function hrefFor(tab: Tab, entries: readonly CollectionEntryDTO[] | null): string {
  if (tab.kind === 'song') return `/songs/${encodeURIComponent(tab.songId)}`;
  const ordered = orderedEntries(entries ?? []);
  let k = tab.entryId ? ordered.findIndex((e) => e.entryId === tab.entryId) : -1;
  if (k < 0 && tab.songId) k = resolveSetlistIndex(ordered, tab.songId, 0);
  if (k < 0 && ordered.length > 0) k = 0;
  const entry = ordered[k];
  if (!entry) {
    const eventId = practiceEventId(tab.collectionId);
    return eventId ? `/events/${encodeURIComponent(eventId)}` : `/collections/${encodeURIComponent(tab.collectionId)}`;
  }
  return `/songs/${encodeURIComponent(entry.songId)}${setlistSearch(new URLSearchParams(), tab.collectionId, k, entry.entryId)}`;
}

/* ─── Stored doc (Dexie kgr.kv) ─────────────────────────────────────────── */

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const isId = (v: unknown): v is string => typeof v === 'string' && v.length > 0 && v.length <= 128;
const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;

function parsePage(v: unknown): PageMemo | null {
  if (!isObj(v) || !isId(v.fileId) || !isCount(v.rev) || !isCount(v.page)) return null;
  return { fileId: v.fileId, rev: v.rev, page: v.page };
}

function parseTab(v: unknown): Tab | null {
  if (!isObj(v)) return null;
  const usedAt = typeof v.usedAt === 'number' && Number.isFinite(v.usedAt) ? v.usedAt : 0;
  if (v.kind === 'song') {
    if (!isId(v.songId)) return null;
    return { kind: 'song', key: songTabKey(v.songId), songId: v.songId, sheetId: isId(v.sheetId) ? v.sheetId : null, page: parsePage(v.page), usedAt };
  }
  if (v.kind === 'collection') {
    if (!isId(v.collectionId)) return null;
    const sheets: Record<string, string> = {};
    if (isObj(v.sheets)) {
      for (const [songId, sheetId] of Object.entries(v.sheets).slice(-MAX_LIST_SHEETS)) if (isId(songId) && isId(sheetId)) sheets[songId] = sheetId;
    }
    return {
      kind: 'collection',
      key: collectionTabKey(v.collectionId),
      collectionId: v.collectionId,
      entryId: isId(v.entryId) ? v.entryId : null,
      songId: isId(v.songId) ? v.songId : null,
      sheets,
      page: parsePage(v.page),
      usedAt,
    };
  }
  return null;
}

/**
 * The stored doc of `userId`. Another user's doc, another version, or anything that is not a doc
 * gives an empty doc; unreadable tabs (and repeats of a key) are dropped, the rest kept.
 */
export function parseDoc(raw: unknown, userId: string): TabsDoc {
  if (!isObj(raw) || raw.v !== TABS_VERSION || raw.userId !== userId || !Array.isArray(raw.items)) return emptyDoc(userId);
  const items: Tab[] = [];
  const keys = new Set<string>();
  for (const v of raw.items) {
    const tab = parseTab(v);
    if (!tab || keys.has(tab.key)) continue;
    keys.add(tab.key);
    items.push(tab);
  }
  return { v: TABS_VERSION, userId, items: items.slice(0, MAX_TABS) };
}
